import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { LanguageToggleWidget } from '../components/marketing/LanguageToggleWidget'
import { createPilotLead } from '../api/pilotLeads'
import { isValidEmail, normalizePhone, toE164 } from '../lib/utils'
import { PhoneInput } from '../components/ui/PhoneInput'

// Voysi flamingo marketing site. 1:1 TSX port of
// ui_kits/marketing-site/components.jsx. The ContactForm submits to the
// public ``POST /pilot-leads`` endpoint — a row is persisted server-side
// and an email alert fires via Resend to the triage inbox. The `contacts`
// table remains off-limits per CLAUDE.md; pilot leads live in their own
// table.
//
// All visible copy is wired through the `marketing` i18n namespace so the
// floating language widget can flip EN ↔ ES without forking the page.

// ── Auth gate ──────────────────────────────────────────────────────────────
// We can't use ``useMe()`` here: ``apiFetch`` redirects to WorkOS on 401 by
// design, which would bounce every cold visitor to the login screen before
// the marketing page can render. Instead, do a raw ``fetch`` that ignores
// the 401 silently and only navigates when the session is genuinely live.

export function HomePage() {
  useEffect(() => {
    const ctrl = new AbortController()
    fetch('/api/auth/me', { signal: ctrl.signal, credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { user?: unknown } | null) => {
        if (data?.user) window.location.assign('/app')
      })
      .catch(() => {})
    return () => ctrl.abort()
  }, [])
  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-sans text-ink-900">
      <SiteNav />
      <Hero />
      <FeatureGrid />
      <PilotSection />
      <AddOns />
      <ContactForm />
      <CTASection />
      <Footer />
      <LanguageToggleWidget />
    </div>
  )
}

// ── Site nav ───────────────────────────────────────────────────────────────
function SiteNav() {
  const { t } = useTranslation('marketing')
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])
  const links = [
    { label: t('nav.product'), href: '#product' },
    { label: t('nav.pilot'), href: '#pilot' },
    { label: t('nav.contact'), href: '#contact' },
  ]
  return (
    <nav className="relative mx-auto flex max-w-[1280px] items-center justify-between px-5 py-5 md:px-12">
      <a href="#" className="flex items-center gap-2.5 no-underline">
        <img src="/brand/logo-mark.svg" width={36} height={36} alt="Voysi" />
        <span className="font-display text-[20px] font-extrabold tracking-tight text-ink-900">
          Voysi<span className="text-flamingo-500">AI</span>
        </span>
      </a>
      <div className="hidden items-center gap-7 md:flex">
        {links.map((l) => (
          <a
            key={l.label}
            href={l.href}
            className="text-[14px] font-medium text-ink-700 no-underline"
          >
            {l.label}
          </a>
        ))}
      </div>
      <div className="hidden gap-2.5 md:flex">
        <a href="/app" className={btnClass('ghost')}>
          {t('nav.signIn')}
        </a>
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t('nav.closeMenu') : t('nav.openMenu')}
        aria-expanded={open}
        className="relative z-[60] flex h-10 w-10 items-center justify-center text-flamingo-500 md:hidden"
      >
        <Menu
          aria-hidden="true"
          className={`absolute h-6 w-6 transition-transform duration-[var(--dur-slow)] ease-[var(--ease-out)] ${
            open ? '-rotate-90 opacity-0' : 'rotate-0 opacity-100'
          }`}
        />
        <X
          aria-hidden="true"
          className={`absolute h-6 w-6 transition-transform duration-[var(--dur-slow)] ease-[var(--ease-out)] ${
            open ? 'rotate-0 opacity-100' : 'rotate-90 opacity-0'
          }`}
        />
      </button>
      <div
        aria-hidden="true"
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-40 bg-ink-900/40 transition-opacity duration-[var(--dur-slow)] ease-[var(--ease-out)] md:hidden ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <aside
        aria-hidden={!open}
        className={`fixed inset-y-0 right-0 z-50 w-[84%] max-w-[320px] bg-white shadow-xl transition-transform duration-[var(--dur-slow)] ease-[var(--ease-out)] md:hidden ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col gap-2 px-6 pb-8 pt-24">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className="rounded-lg px-2 py-3 text-[16px] font-semibold text-ink-900 no-underline hover:bg-ink-50"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/app"
            onClick={() => setOpen(false)}
            className="mt-2 rounded-lg px-2 py-3 text-[16px] font-semibold text-ink-700 no-underline hover:bg-ink-50"
          >
            {t('nav.signIn')}
          </a>
        </div>
      </aside>
    </nav>
  )
}

// ── Hero ───────────────────────────────────────────────────────────────────
function Hero() {
  const { t } = useTranslation('marketing')
  return (
    <section className="relative mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-14 px-5 py-16 pb-24 md:px-12 lg:grid-cols-[1.1fr_1fr]">
      <div>
        <div
          className="mb-6 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-semibold text-flamingo-700"
          style={{ background: '#FFF1F6' }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full bg-flamingo-500"
            style={{ animation: 'voysiPulse 1.4s infinite' }}
          />
          {t('hero.badge')}
        </div>
        <h1 className="m-0 font-display text-[44px] font-extrabold leading-[1.05] tracking-tight text-ink-900 md:text-[64px]">
          {t('hero.h1Prefix')}
          <span className="text-flamingo-500">
            {t('hero.h1HighlightSmall')}
            <span className="relative inline-block">
              {t('hero.h1HighlightBusinesses')}
              <svg
                className="absolute -bottom-2.5 left-0 right-0 w-full"
                viewBox="0 0 300 10"
                preserveAspectRatio="none"
              >
                <path
                  d="M 2 7 Q 150 -2, 298 5"
                  stroke="#FF3D84"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </span>
          .
          <br />
          {t('hero.h1Tail')}
        </h1>
        <p className="mt-6 max-w-[520px] text-[19px] leading-[1.55] text-ink-600">
          {t('hero.subtitle')}
        </p>
        <div className="mt-8 flex gap-3">
          <a href="#contact" className={btnClass('primary', 'lg')}>
            {t('hero.ctaApply')}
          </a>
        </div>
      </div>
      <DemoCard />
    </section>
  )
}

// ── Demo card (animated) ───────────────────────────────────────────────────
function DemoCard() {
  const { t } = useTranslation('marketing')
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((tt) => tt + 1), 2800)
    return () => clearInterval(id)
  }, [])
  // Lines are sourced from the marketing namespace so they flip with the UI
  // language. `returnObjects: true` returns the JSON array as-is.
  const lines = t('demo.lines', { returnObjects: true }) as Array<{
    role: 'agent' | 'caller'
    text: string
  }>
  const shown = lines.slice(0, (tick % lines.length) + 1)
  return (
    <div
      className="relative rounded-3xl p-4 md:p-6"
      style={{
        background: 'linear-gradient(160deg,#FFF1F6 0%,#FFD8E5 100%)',
        boxShadow: '0 24px 64px rgba(255,61,132,0.18)',
      }}
    >
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-flamingo-500"
            >
              <img src="/brand/logo-mark.svg" width={26} height={26} alt="" />
            </div>
            <div>
              <div className="text-[14px] font-bold">{t('demo.agentTitle')}</div>
              <div className="font-mono text-[11px] text-ink-500">{t('demo.agentPhone')}</div>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{ background: 'rgba(29,185,84,0.15)', color: '#0A7A36' }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full bg-success"
              style={{ animation: 'voysiPulse 1.4s infinite' }}
            />
            {t('demo.liveLabel')} 00:{(42 + tick * 3).toString().padStart(2, '0')}
          </span>
        </div>
        <div className="flex min-h-[180px] flex-col gap-1.5 md:min-h-[210px]">
          {shown.map((l, i) => (
            <div
              key={i}
              className={`flex flex-col ${l.role === 'agent' ? 'items-start' : 'items-end'}`}
            >
              <div
                className="max-w-[90%] px-3 py-2 text-[12.5px] leading-[1.4] md:max-w-[86%] md:px-3.5 md:py-2.5 md:text-[13px]"
                style={{
                  borderRadius: 14,
                  borderTopLeftRadius: l.role === 'agent' ? 4 : 14,
                  borderTopRightRadius: l.role === 'agent' ? 14 : 4,
                  background: l.role === 'agent' ? '#FFF1F6' : '#F3F2EF',
                  color: l.role === 'agent' ? '#56062A' : '#1A181D',
                }}
              >
                {l.text}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-ink-100 pt-3">
          <div className="flex items-center gap-2">
            <MarketingWave />
            <span className="font-mono text-[11px] text-ink-500">{t('demo.voiceLabel')}</span>
          </div>
          <div className="flex gap-1.5">
            <span
              className="rounded-md px-2 py-0.5 text-[10px] font-bold"
              style={{ background: '#FFF1F6', color: '#C4125C' }}
            >
              {t('demo.badgeConfirmed')}
            </span>
            <span
              className="rounded-md px-2 py-0.5 text-[10px] font-bold"
              style={{ background: '#F3F2EF', color: '#49474A' }}
            >
              {t('demo.badgeBilingual')}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Tighter 10-bar wave used by the DemoCard + ContactForm header. Mirrors
// components.jsx::Wave. We keep this local so it doesn't drift if the
// shared `Waveform` primitive evolves for the dashboard.
function MarketingWave() {
  return (
    <div className="flex items-center gap-[2px]" style={{ height: 18 }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 2,
            height: '100%',
            background: 'linear-gradient(to bottom,#FF3D84,#FF7FAC)',
            borderRadius: 2,
            transformOrigin: 'center',
            animation: `voysiBar 0.${5 + (i % 4)}s ease-in-out infinite alternate`,
            animationDelay: `${i * 0.06}s`,
          }}
        />
      ))}
    </div>
  )
}

// ── Feature grid ───────────────────────────────────────────────────────────
function FeatureGrid() {
  const { t } = useTranslation('marketing')
  const feats = t('features.items', { returnObjects: true }) as Array<{
    k: string
    t: string
    b: string
  }>
  return (
    <section id="product" className="mx-auto max-w-[1280px] px-5 py-20 md:px-12 md:py-24">
      <div className="max-w-[760px]">
        <div className="text-[13px] font-bold uppercase tracking-[0.08em] text-flamingo-700">
          {t('features.eyebrow')}
        </div>
        <h2 className="mt-2 font-display text-[44px] font-extrabold leading-[1.1] tracking-tight text-ink-900">
          {t('features.h2')}
        </h2>
      </div>
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {feats.map((f) => (
          <div
            key={f.k}
            className="rounded-2xl border border-ink-200 bg-white p-6"
          >
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl font-mono font-bold"
              style={{ background: '#FFF1F6', color: '#C4125C' }}
            >
              {f.k}
            </div>
            <div className="mt-5 text-[17px] font-bold tracking-tight">{f.t}</div>
            <div className="mt-1.5 text-[14px] leading-[1.55] text-ink-600">{f.b}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Pilot section ──────────────────────────────────────────────────────────
function PilotSection() {
  const { t } = useTranslation('marketing')
  return (
    <section
      id="pilot"
      className="px-5 py-20 text-white md:px-12 md:py-24"
      style={{ background: '#0E0D11' }}
    >
      <div className="mx-auto max-w-[820px] text-center">
        <div className="text-[13px] font-bold uppercase tracking-[0.08em] text-flamingo-300">
          {t('pilot.eyebrow')}
        </div>
        <h2 className="mt-3 font-display text-[40px] font-extrabold leading-[1.1] tracking-tight md:text-[52px]">
          {t('pilot.h2')}
        </h2>
        <p className="mx-auto mt-6 max-w-[640px] text-[17px] leading-[1.6] text-ink-300">
          {t('pilot.p')}
        </p>
        <div
          className="mx-auto mt-10 inline-block max-w-full rounded-2xl px-6 py-5 md:px-8 md:py-6"
          style={{ background: 'rgba(255,61,132,0.12)', border: '1px solid rgba(255,61,132,0.35)' }}
        >
          <div className="font-display text-[22px] font-bold leading-[1.3] tracking-tight text-white md:text-[26px]">
            {t('pilot.pricingHeadPrefix')}
            <span className="text-flamingo-500">{t('pilot.pricingHeadHighlight')}</span>
            {t('pilot.pricingHeadSuffix')}
          </div>
          <div className="mt-1.5 text-[14px] text-ink-300">{t('pilot.pricingSub')}</div>
        </div>
        <p className="mt-8 text-[14px] text-ink-400">{t('pilot.currentPilot')}</p>
        <div className="mt-8 flex justify-center">
          <a href="#contact" className={btnClass('primary', 'lg')}>
            {t('pilot.ctaApply')}
          </a>
        </div>
      </div>
    </section>
  )
}

// ── Add-ons ───────────────────────────────────────────────────────────────
function AddOns() {
  const { t } = useTranslation('marketing')
  const items = t('addOns.items', { returnObjects: true }) as string[]
  return (
    <section className="mx-auto max-w-[1280px] px-5 py-16 md:px-12 md:py-20">
      <div className="max-w-[640px]">
        <h3 className="m-0 font-display text-[24px] font-bold tracking-tight text-ink-900 md:text-[28px]">
          {t('addOns.h3')}
        </h3>
        <p className="mt-2 text-[15px] leading-[1.55] text-ink-500">{t('addOns.p')}</p>
      </div>
      <ul className="mt-8 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((label) => (
          <li
            key={label}
            className="rounded-xl border border-ink-200 bg-white px-4 py-3.5 text-[14px] font-semibold text-ink-700"
          >
            {label}
          </li>
        ))}
      </ul>
    </section>
  )
}

// ── Contact form ───────────────────────────────────────────────────────────
type ContactField = 'name' | 'business' | 'phone' | 'email' | 'useCase'

// Belt-and-suspenders fallback for the case where the backend POST fails
// entirely (network down, server 500, Railway outage). Builds a `mailto:`
// link with the user's typed values URL-encoded into the body so the lead
// still reaches the same inbox the Resend integration targets.
function buildMailtoHref(form: Record<ContactField, string>): string {
  const body =
    `Name: ${form.name}\n` +
    `Business: ${form.business}\n` +
    `Phone: ${form.phone}\n` +
    `Email: ${form.email}\n\n` +
    `Use case:\n${form.useCase}`
  const subject = `Pilot application — ${form.name || 'applicant'}`
  return (
    `mailto:carlosmasson@voysi.ai` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`
  )
}

function ContactForm() {
  const { t } = useTranslation('marketing')
  const [form, setForm] = useState<Record<ContactField, string>>({
    name: '',
    business: '',
    phone: '',
    email: '',
    useCase: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailTouched, setEmailTouched] = useState(false)
  const set =
    (k: ContactField) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }))

  const phoneComplete = normalizePhone(form.phone).length === 10
  const emailInvalid = form.email.trim().length > 0 && !isValidEmail(form.email.trim())
  const canSubmit = phoneComplete && !emailInvalid

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await createPilotLead({
        name: form.name.trim(),
        business: form.business.trim(),
        phone: toE164(form.phone),
        email: form.email.trim(),
        use_case: form.useCase.trim(),
      })
      setSubmitted(true)
    } catch (err) {
      // Keep the typed values intact so the user can retry without
      // re-entering everything. Surface a friendly inline error plus a
      // `mailto:` fallback button (rendered below).
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong submitting the form.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const inputFields: {
    id: string
    label: string
    type: string
    key: Exclude<ContactField, 'useCase'>
    placeholder: string
    mono: boolean
  }[] = [
    {
      id: 'v-name',
      label: t('contact.fields.name'),
      type: 'text',
      key: 'name',
      placeholder: t('contact.fields.namePlaceholder'),
      mono: false,
    },
    {
      id: 'v-business',
      label: t('contact.fields.business'),
      type: 'text',
      key: 'business',
      placeholder: t('contact.fields.businessPlaceholder'),
      mono: false,
    },
    {
      id: 'v-phone',
      label: t('contact.fields.phone'),
      type: 'tel',
      key: 'phone',
      placeholder: t('contact.fields.phonePlaceholder'),
      mono: true,
    },
    {
      id: 'v-email',
      label: t('contact.fields.email'),
      type: 'email',
      key: 'email',
      placeholder: t('contact.fields.emailPlaceholder'),
      mono: false,
    },
  ]

  return (
    <section id="contact" className="mx-auto max-w-[1280px] px-5 py-20 md:px-12 md:py-24">
      <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-[1fr_1.05fr]">
        <div>
          <div className="text-[13px] font-bold uppercase tracking-[0.08em] text-flamingo-700">
            {t('contact.eyebrow')}
          </div>
          <h2 className="mb-5 mt-2 font-display text-[36px] font-extrabold leading-[1.05] tracking-tight md:text-[48px]">
            {t('contact.h2Prefix')}
            <span className="text-flamingo-500">{t('contact.h2Highlight')}</span>
            {t('contact.h2Suffix')}
          </h2>
          <p className="m-0 max-w-[480px] text-[17px] leading-[1.55] text-ink-600">
            {t('contact.p')}
          </p>
          <p className="mt-6 max-w-[480px] text-[14px] leading-[1.55] text-ink-500">
            {t('contact.directReachPrefix')}
            <a
              href="mailto:carlosmasson@voysi.ai"
              className="text-ink-700 underline decoration-flamingo-300 underline-offset-2 hover:text-flamingo-700"
            >
              carlosmasson@voysi.ai
            </a>
            {t('contact.directReachSuffix')}
          </p>
        </div>

        <div
          className="rounded-3xl p-1.5"
          style={{
            background: 'linear-gradient(160deg,#FFF1F6 0%,#FFD8E5 100%)',
            boxShadow: '0 24px 64px rgba(255,61,132,0.18)',
          }}
        >
          <div className="rounded-[20px] bg-white px-5 pb-7 pt-7 md:px-9 md:pb-8 md:pt-9">
            {submitted ? (
              <div className="px-2 py-10 text-center">
                <div
                  className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full text-[28px] font-extrabold"
                  style={{ background: '#FFF1F6', color: '#C4125C' }}
                >
                  ✓
                </div>
                <div className="font-display text-[28px] font-extrabold tracking-tight">
                  {t('contact.successTitlePrefix')}
                  {form.name.split(' ')[0] || t('contact.successFriendFallback')}.
                </div>
                <p className="mt-2.5 text-[15px] leading-[1.55] text-ink-600">
                  {t('contact.successMessagePrefix')}
                  <span className="text-ink-900">
                    {form.email || t('contact.successEmailFallback')}
                  </span>
                  {t('contact.successMessageSuffix')}
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <div className="mb-1 flex items-center gap-2.5">
                  <MarketingWave />
                  <span className="font-mono text-[11px] tracking-[0.04em] text-ink-500">
                    {t('contact.formEyebrow')}
                  </span>
                </div>
                {inputFields.map((f) => {
                  const isPhone = f.key === 'phone'
                  const isEmail = f.key === 'email'
                  const showEmailError = isEmail && emailTouched && emailInvalid
                  return (
                    <div key={f.id} className="flex flex-col gap-1.5">
                      <label
                        htmlFor={f.id}
                        className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-600"
                      >
                        {f.label}
                      </label>
                      {isPhone ? (
                        <PhoneInput
                          id={f.id}
                          required
                          placeholder={f.placeholder}
                          value={form.phone}
                          onChange={(m) =>
                            setForm((prev) => ({ ...prev, phone: m }))
                          }
                          className={
                            'w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-ink-900 outline-none transition-colors ' +
                            (showEmailError
                              ? 'border-error focus:border-error focus:ring-error'
                              : 'border-ink-200 focus:border-flamingo-500') +
                            (f.mono ? ' font-mono' : '')
                          }
                        />
                      ) : (
                        <input
                          id={f.id}
                          required
                          type={f.type}
                          placeholder={f.placeholder}
                          value={form[f.key]}
                          onChange={set(f.key)}
                          onBlur={isEmail ? () => setEmailTouched(true) : undefined}
                          aria-invalid={showEmailError || undefined}
                          className={
                            'w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-ink-900 outline-none transition-colors ' +
                            (showEmailError
                              ? 'border-error focus:border-error focus:ring-error'
                              : 'border-ink-200 focus:border-flamingo-500') +
                            (f.mono ? ' font-mono' : '')
                          }
                        />
                      )}
                      {showEmailError && (
                        <p className="text-xs text-error">
                          {t('contact.fields.emailInvalid')}
                        </p>
                      )}
                    </div>
                  )
                })}
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="v-usecase"
                    className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-600"
                  >
                    {t('contact.fields.useCase')}
                  </label>
                  <textarea
                    id="v-usecase"
                    required
                    rows={4}
                    placeholder={t('contact.fields.useCasePlaceholder')}
                    value={form.useCase}
                    onChange={set('useCase')}
                    className="w-full resize-y rounded-xl border border-ink-200 bg-white px-4 py-3.5 text-[15px] leading-[1.5] text-ink-900 outline-none transition-colors focus:border-flamingo-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !canSubmit}
                  className={
                    btnClass('primary', 'lg') +
                    ' mt-2 w-full justify-center' +
                    (submitting || !canSubmit ? ' opacity-70 cursor-not-allowed' : '')
                  }
                >
                  {submitting ? t('contact.submitting') : t('contact.submit')}
                </button>
                {error ? (
                  <div className="mt-2 rounded-xl border border-flamingo-200 bg-flamingo-50 px-4 py-3 text-[13px] leading-[1.45] text-flamingo-900">
                    <div className="mb-2 font-semibold">
                      {t('contact.errorTitle')}
                    </div>
                    <div className="mb-3 text-flamingo-800">
                      {t('contact.errorBody')}
                    </div>
                    <a
                      href={buildMailtoHref(form)}
                      className="inline-flex items-center justify-center rounded-full border border-flamingo-300 bg-white px-4 py-2 text-[13px] font-semibold text-flamingo-700 no-underline hover:bg-flamingo-100"
                    >
                      {t('contact.errorMailto')}
                    </a>
                  </div>
                ) : null}
                <div className="text-center text-[12px] leading-[1.5] text-ink-500">
                  {t('contact.fineprint')}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Final CTA ──────────────────────────────────────────────────────────────
function CTASection() {
  const { t } = useTranslation('marketing')
  return (
    <section className="px-5 py-16 md:px-12 md:py-20">
      <div
        className="relative mx-auto max-w-[1100px] overflow-hidden rounded-[28px] px-6 py-12 md:px-14 md:py-16"
        style={{ background: 'linear-gradient(135deg,#FF3D84 0%,#FF7FAC 100%)' }}
      >
        {/* Flamingo silhouette watermark. The PNG is gray on transparent;
            the CSS filter recolors it to white so it reads as a soft
            highlight on the pink gradient. Source: branding/Logo_Shape.png
            (95x186, ~1:1.96). Sized + positioned to sit fully inside the
            card on the right side, vertically centered, so it reads as a
            decorative mark rather than dominating the layout. */}
        <img
          src="/brand/flamingo-shape.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute right-4 top-1/2 h-[140px] w-[72px] -translate-y-1/2 opacity-15 md:right-10 md:h-[200px] md:w-[102px] md:opacity-25"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
        <h2 className="m-0 max-w-[560px] font-display text-[36px] font-extrabold leading-[1.1] tracking-tight text-white md:text-[48px]">
          {t('cta.h2')}
        </h2>
        <p className="mt-4 max-w-[520px] text-[17px] text-flamingo-50">{t('cta.p')}</p>
        <div className="mt-7 flex gap-3">
          <a
            href="#contact"
            className="cursor-pointer rounded-full px-6 py-3.5 text-[15px] font-semibold text-white no-underline"
            style={{ background: '#0E0D11' }}
          >
            {t('cta.ctaApply')}
          </a>
        </div>
      </div>
    </section>
  )
}

// ── Footer ─────────────────────────────────────────────────────────────────
function Footer() {
  const { t } = useTranslation('marketing')
  return (
    <footer className="px-5 py-10 text-ink-400 md:px-12 md:py-12" style={{ background: '#0E0D11' }}>
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-start gap-10 md:grid-cols-2">
        <div>
          <div className="mb-4 flex items-center gap-2.5">
            <img src="/brand/logo-mark.svg" width={32} height={32} alt="Voysi" />
            <span className="font-display text-[18px] font-extrabold text-white">
              Voysi<span className="text-flamingo-500">AI</span>
            </span>
          </div>
          <p className="m-0 max-w-[280px] text-[13px] leading-[1.55]">{t('footer.tagline')}</p>
        </div>
        <div className="md:text-right">
          <div className="mb-2 text-[13px] font-bold text-white">{t('footer.contactLabel')}</div>
          <a
            href="mailto:carlosmasson@voysi.ai"
            className="text-[13px] text-ink-300 underline decoration-flamingo-500 underline-offset-2 hover:text-white"
          >
            carlosmasson@voysi.ai
          </a>
        </div>
      </div>
      <div className="mx-auto mt-8 flex max-w-[1280px] flex-col justify-between gap-2 border-t border-[#27242B] pt-6 text-[12px] md:flex-row">
        <span>{t('footer.copyright')}</span>
      </div>
    </footer>
  )
}

// ── Button helper ──────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'secondary' | 'ghost'
type BtnSize = 'md' | 'lg'

function btnClass(variant: BtnVariant, size: BtnSize = 'md'): string {
  const base =
    'inline-flex items-center gap-2 rounded-full border-0 font-semibold tracking-tight transition-transform active:scale-[0.97] no-underline cursor-pointer'
  const sizes: Record<BtnSize, string> = {
    md: 'px-[18px] py-2.5 text-[14px]',
    lg: 'px-6 py-3.5 text-[15px]',
  }
  const variants: Record<BtnVariant, string> = {
    primary: 'bg-flamingo-500 text-white hover:bg-flamingo-600',
    secondary: 'bg-white text-ink-900 border border-ink-200 hover:bg-ink-50',
    ghost: 'bg-transparent text-ink-900 hover:bg-ink-100',
  }
  return [base, sizes[size], variants[variant]].join(' ')
}
